import { Box } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { editProjectDetails, qk } from 'api'
import { DialogContent, ToastContent } from 'elements/feedback'
import { renderTextField } from 'elements/inputs'
import { useValidations } from 'lib/form-utils'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const formId = 'EDIT_PROJECT_INFO_FORM'

type FormValues = {
  description: string
  name: string
}

type EditProjectInfoFormProps = {
  projectId: string
  description: string
  name: string
  onClose: () => void
}

export const EditProjectInfoForm = ({
  projectId,
  description,
  name,
  onClose,
}: EditProjectInfoFormProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const rules = useValidations()

  const $editProjectDetails = useMutation(editProjectDetails)

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      description,
      name,
    },
  })

  return (
    <DialogContent
      title={t('projects.settings.edit_project_information')}
      isActionLoading={$editProjectDetails.isLoading}
      externalFormId={formId}
      onDeny={onClose}
      denyText={t('common.cancel')}
      isConfirmDisabled={!isDirty}
    >
      <Box
        id={formId}
        component="form"
        onSubmit={handleSubmit(values =>
          $editProjectDetails.mutate(
            { projectId, ...values },
            {
              onSuccess: () => {
                onClose()
                queryClient.invalidateQueries(
                  qk.projects.details.toKeyWithArgs({ projectId }),
                )
                toast.success(
                  <ToastContent
                    title={t('projects.settings.project_information_updated')}
                  />,
                )
              },
            },
          ),
        )}
      >
        <Controller
          name="name"
          control={control}
          rules={{ ...rules.required, validate: rules.maxLength(200) }}
          render={renderTextField({
            label: t('projects.create.project_name'),
          })}
        />

        <Controller
          name="description"
          control={control}
          rules={{ ...rules.required, validate: rules.maxLength(400) }}
          render={renderTextField({
            rows: 3,
            multiline: true,
            label: t('projects.create.project_description'),
          })}
        />
      </Box>
    </DialogContent>
  )
}
